import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPreferences, updatePreferences } from "../../api/accountApi";
import { useLocale, useCurrency } from "../../intl";
import { writeLocaleToStorage } from "../../intl/localeResolution.js";
import { writeDisplayCurrencyToStorage } from "../../intl/currencyResolution.js";
import {
  FX_CURRENCY_ANALYTICS_SURFACES,
  FX_CURRENCY_SOURCE_COMPONENTS,
} from "../../constants/fxCurrencyAnalyticsConstants.js";

const PreferencesPage = () => {
  const { locale, setLocale, t } = useLocale();
  const { displayCurrency, setDisplayCurrency } = useCurrency();
  const [preferences, setPreferences] = useState({
    theme: "system",
    defaultMapView: "map",
    defaultRealEstateMode: "buy",
    emailNotifications: true,
    marketingEmails: false,
    locale: "es-CO",
    displayCurrency: "COP",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        const body = await getPreferences();
        if (!cancelled) {
          const nextPreferences = {
            ...preferences,
            ...body.preferences,
            locale: body.preferences?.locale || locale,
            displayCurrency: body.preferences?.displayCurrency || displayCurrency,
          };
          setPreferences(nextPreferences);
          if (body.preferences?.locale) {
            setLocale(body.preferences.locale);
            writeLocaleToStorage(body.preferences.locale);
          }
          if (body.preferences?.displayCurrency) {
            setDisplayCurrency(body.preferences.displayCurrency, { trackChange: false });
            writeDisplayCurrencyToStorage(body.preferences.displayCurrency);
          }
          if (body.preferences?.theme) {
            document.documentElement.dataset.theme = body.preferences.theme;
            try {
              localStorage.setItem("midpoint_theme", body.preferences.theme);
            } catch {
              // ignore storage errors
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error.message || t("preferences.loadError"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const body = await updatePreferences({
        ...preferences,
        locale,
        displayCurrency,
      });
      setPreferences(body.preferences);
      if (body.preferences?.locale) {
        setLocale(body.preferences.locale);
      }
      if (body.preferences?.displayCurrency) {
        setDisplayCurrency(body.preferences.displayCurrency, { trackChange: false });
      }
      if (body.preferences?.theme) {
        document.documentElement.dataset.theme = body.preferences.theme;
        try {
          localStorage.setItem("midpoint_theme", body.preferences.theme);
        } catch {
          // ignore
        }
      }
      setSuccessMessage(t("preferences.saved"));
    } catch (error) {
      setErrorMessage(error.message || t("preferences.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (key, value) => {
    setPreferences((current) => ({ ...current, [key]: value }));
  };

  const handleLocaleChange = (event) => {
    const nextLocale = event.target.value;
    updateField("locale", nextLocale);
    setLocale(nextLocale);
  };

  const handleDisplayCurrencyChange = (event) => {
    const nextCurrency = event.target.value;
    updateField("displayCurrency", nextCurrency);
    setDisplayCurrency(nextCurrency, {
      sourceComponent: FX_CURRENCY_SOURCE_COMPONENTS.ACCOUNT_PREFERENCES_PAGE,
      surface: FX_CURRENCY_ANALYTICS_SURFACES.CURRENCY_CHANGE,
    });
  };

  if (isLoading) {
    return <p className="muted panel-card">{t("preferences.loading")}</p>;
  }

  return (
    <>
      {successMessage ? (
        <section className="panel-card account-security-flash">
          <p>{successMessage}</p>
        </section>
      ) : null}

      {errorMessage ? (
        <section className="panel-card">
          <p className="admin-data-health-error">{errorMessage}</p>
        </section>
      ) : null}

      <form className="panel-card admin-users-form" onSubmit={handleSubmit}>
        <h2>{t("preferences.title")}</h2>
        <p className="muted admin-users-help">
          Theme and map defaults are saved here. For saved search and favorite listing email alerts,
          use <Link to="/account/notifications">Notifications</Link>.
        </p>

        <label className="admin-users-field">
          <span>{t("preferences.languageLabel")}</span>
          <select value={locale} onChange={handleLocaleChange}>
            <option value="es-CO">{t("preferences.localeEsCo")}</option>
            <option value="en-US">{t("preferences.localeEnUs")}</option>
          </select>
          <span className="muted admin-users-help">{t("preferences.languageHelp")}</span>
        </label>

        <label className="admin-users-field">
          <span>{t("preferences.displayCurrencyLabel")}</span>
          <select value={displayCurrency} onChange={handleDisplayCurrencyChange}>
            <option value="COP">{t("preferences.currencyCop")}</option>
            <option value="USD">{t("preferences.currencyUsd")}</option>
            <option value="EUR">{t("preferences.currencyEur")}</option>
          </select>
          <span className="muted admin-users-help">{t("preferences.displayCurrencyHelp")}</span>
        </label>

        <label className="admin-users-field">
          <span>Theme</span>
          <select
            value={preferences.theme}
            onChange={(event) => updateField("theme", event.target.value)}
          >
            <option value="system">System</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </label>

        <label className="admin-users-field">
          <span>Default map view</span>
          <select
            value={preferences.defaultMapView}
            onChange={(event) => updateField("defaultMapView", event.target.value)}
          >
            <option value="map">Map</option>
            <option value="list">List</option>
            <option value="split">Split</option>
          </select>
        </label>

        <label className="admin-users-field">
          <span>Default real estate mode</span>
          <select
            value={preferences.defaultRealEstateMode}
            onChange={(event) => updateField("defaultRealEstateMode", event.target.value)}
          >
            <option value="buy">Buy</option>
            <option value="rent">Rent</option>
          </select>
        </label>

        <label className="admin-users-checkbox">
          <input
            type="checkbox"
            checked={Boolean(preferences.emailNotifications)}
            onChange={(event) => updateField("emailNotifications", event.target.checked)}
          />
          Email notifications
        </label>

        <label className="admin-users-checkbox">
          <input
            type="checkbox"
            checked={Boolean(preferences.marketingEmails)}
            onChange={(event) => updateField("marketingEmails", event.target.checked)}
          />
          Marketing emails
        </label>

        <button type="submit" className="report-submit-cta" disabled={isSaving}>
          {isSaving ? t("preferences.saving") : t("preferences.saveButton")}
        </button>
      </form>
    </>
  );
};

export default PreferencesPage;
